'use client';

import { useRef } from 'react';
import { cn } from '../_lib/utils';
import { CardsStackItem, type CardsStackItemProps } from '../CardsStackItem';
import { motion, useScroll, useTransform } from 'motion/react';
import { useGetPageCurrentScrollContainerRef } from '../LenisScroll';

export type CardsStackProps = {
	className?: string;
	cards: CardsStackItemProps[];
};

export function CardsStack(props: CardsStackProps) {
	const cardsContainerRef = useRef<HTMLDivElement>(null);

	const { scrollWrapperRef } = useGetPageCurrentScrollContainerRef();

	const scaleStep = props.cards.length < 5 ? 0.1 : 0.3 / (props.cards.length - 1);

	return (
		<article data-component='CardSectionList' className={cn('container-xl py-5 md:py-[7.5rem]', props.className)}>
			<div
				ref={cardsContainerRef}
				className='flex flex-col gap-40'
				style={{
					marginBottom: `${(props.cards.length - 1) * 3.5}rem`,
				}}
			>
				{props.cards.map((card, i) => {
					const { scrollYProgress } = useScroll({
						container: scrollWrapperRef,
						target: cardsContainerRef,
						layoutEffect: false,
						offset: [`${(i / props.cards.length) * 100}%`, `${((i + 1) / props.cards.length) * 100}%`],
					});

					const reverseIndex = props.cards.length - (i + 1);
					const scale = useTransform(scrollYProgress, [0, 1], [1, 1 - scaleStep * reverseIndex]);

					return (
						<motion.div
							key={i}
							className='will-change-transform sticky top-[var(--header-offset,0)]'
							style={{ transformOrigin: '50% 0', scale, paddingTop: `${i * 2}rem`, marginBottom: `-${i * 2}rem` }}
						>
							{i !== 0 && (
								<div className='absolute -top-[6.375rem] md:-top-32 w-full h-[16.875rem] md:h-80 bg-linear-to-b from-background-primary/0 to-background-primary pointer-events-none' />
							)}

							<CardsStackItem {...card} textPosition={card.textPosition ?? (i % 2 === 0 ? 'left' : 'right')} />
						</motion.div>
					);
				})}
			</div>
		</article>
	);
}
